import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { apiService, ApiKey } from '../services/api';
import { useAuth } from './AuthContext';
import { useImpersonation } from './ImpersonationContext';
import { useOrganizations } from './OrganizationContext';
import type { ApiKeyDataScope } from '../types/apiKeyDataScope';
import {
  effectiveApiKeyOrganizationId,
  selectApiKeyForOrganization,
} from '../utils/apiKeyForOrganization';

interface ApiKeyContextType {
  apiKey: string | null;
  setApiKey: (key: string | null) => void;
  apiKeys: ApiKey[];
  isLoading: boolean;
  error: string | null;
  createApiKey: (name: string, permissions?: string[], dataScope?: ApiKeyDataScope | null) => Promise<void>;
  updateApiKey: (
    id: string,
    updates: { name?: string; permissions?: string[]; isActive?: boolean; dataScope?: ApiKeyDataScope | null }
  ) => Promise<void>;
  deleteApiKey: (id: string) => Promise<void>;
  refreshApiKeys: () => Promise<void>;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

export const useApiKey = () => {
  const context = useContext(ApiKeyContext);
  if (context === undefined) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};

interface ApiKeyProviderProps {
  children: ReactNode;
}

/**
 * The selected key is remembered per scope (signed-in user + effective org),
 * never in one shared entry. The old unscoped `apiKey` entry let a platform
 * admin's own key follow them into every store they viewed as (MOT-106).
 */
const API_KEY_STORAGE_PREFIX = 'apiKey:';
const LEGACY_API_KEY_STORAGE = 'apiKey';

const readStoredKey = (scope: string): string | null => {
  try {
    return localStorage.getItem(`${API_KEY_STORAGE_PREFIX}${scope}`);
  } catch {
    return null;
  }
};

const writeStoredKey = (scope: string, key: string | null): void => {
  try {
    if (key) {
      localStorage.setItem(`${API_KEY_STORAGE_PREFIX}${scope}`, key);
    } else {
      localStorage.removeItem(`${API_KEY_STORAGE_PREFIX}${scope}`);
    }
  } catch {
    // Storage can be blocked; the key then simply is not remembered.
  }
};

const removeLegacyStoredKey = (): void => {
  try {
    localStorage.removeItem(LEGACY_API_KEY_STORAGE);
  } catch {
    // best effort
  }
};

const NO_KEYS: ApiKey[] = [];

interface ScopedKey {
  scope: string | null;
  key: string | null;
}

interface ScopedKeys {
  scope: string | null;
  keys: ApiKey[];
}

export const ApiKeyProvider: React.FC<ApiKeyProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { isImpersonating, impersonatedOrg } = useImpersonation();
  const { actingOrganizationId, isLoading: organizationsLoading } = useOrganizations();
  const uid = currentUser?.uid ?? null;

  /**
   * Whose keys these are: the organization the app is effectively acting as.
   * While an admin views as a store that is the store, never the admin's own
   * organization. `null` means there is no org to resolve keys for (signed out,
   * a brand-only "View as" with no organization id, or the acting org still
   * loading); in that state no key is shown, fetched or sent.
   */
  let orgPart: string | null;
  if (isImpersonating) {
    orgPart = impersonatedOrg?.id ? `org:${impersonatedOrg.id}` : null;
  } else if (actingOrganizationId) {
    orgPart = `org:${actingOrganizationId}`;
  } else if (organizationsLoading) {
    orgPart = null;
  } else {
    orgPart = uid ? `user:${uid}` : null;
  }
  const scopeId = uid && orgPart ? `${uid}:${orgPart}` : null;
  const effectiveOrganizationId = effectiveApiKeyOrganizationId(
    isImpersonating,
    impersonatedOrg?.id,
    actingOrganizationId,
  );

  const [keyState, setKeyState] = useState<ScopedKey>({ scope: null, key: null });
  const [keysState, setKeysState] = useState<ScopedKeys>({ scope: null, keys: NO_KEYS });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State written for another scope is never exposed: it reads as empty the
  // moment the effective org changes, before any effect runs.
  const apiKey = scopeId !== null && keyState.scope === scopeId ? keyState.key : null;
  const apiKeys = useMemo(
    () => (scopeId !== null && keysState.scope === scopeId ? keysState.keys : NO_KEYS),
    [scopeId, keysState]
  );

  const scopeRef = useRef<string | null>(scopeId);
  scopeRef.current = scopeId;
  const effectiveOrgRef = useRef<string | null>(effectiveOrganizationId);
  effectiveOrgRef.current = effectiveOrganizationId;
  const apiKeyRef = useRef<string | null>(apiKey);
  apiKeyRef.current = apiKey;

  // Keep the HTTP client's X-API-Key in step with the scoped key. A layout
  // effect so it is already correct before any child effect fires a request,
  // and cleared on unmount so a key can never outlive its provider.
  useLayoutEffect(() => {
    apiService.setApiKey(apiKey);
    return () => {
      apiService.setApiKey(null);
    };
  }, [apiKey]);

  // The unscoped entry written by earlier versions held a key usable across orgs.
  useEffect(() => {
    removeLegacyStoredKey();
  }, []);

  const setApiKey = useCallback((key: string | null) => {
    const scope = scopeRef.current;
    if (!scope) return;
    setKeyState({ scope, key });
    writeStoredKey(scope, key);
  }, []);

  const refreshApiKeys = useCallback(async () => {
    const scope = scopeRef.current;
    if (!scope) return;
    try {
      setIsLoading(true);
      setError(null);
      const fetched = await apiService.getApiKeys();
      // The effective org changed while this was in flight: these keys belong
      // to the org we left, so drop them.
      if (scopeRef.current !== scope) return;
      // The list is every key for this Firebase user, not the org being
      // viewed. A remembered secret or the newest active key from another
      // org must not be stored under this scope.
      const { keys, chosen } = selectApiKeyForOrganization(
        fetched,
        effectiveOrgRef.current,
        [apiKeyRef.current, readStoredKey(scope)],
      );
      setKeysState({ scope, keys });
      setKeyState({ scope, key: chosen });
      writeStoredKey(scope, chosen);
    } catch (err) {
      if (scopeRef.current !== scope) return;
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Re-resolve whenever the effective org changes (View as, Switch, Exit,
  // organization switcher, sign-in/out). Nothing from the previous scope
  // survives: the derived values above already read as empty for the new one.
  useEffect(() => {
    setError(null);
    if (!scopeId) {
      setIsLoading(false);
      return;
    }
    refreshApiKeys();
  }, [scopeId, refreshApiKeys]);

  const createApiKey = async (name: string, permissions?: string[], dataScope?: ApiKeyDataScope | null) => {
    const scope = scopeRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const newKey = await apiService.createApiKey(name, permissions, dataScope);
      if (!scope || scopeRef.current !== scope) return;
      setKeysState((prev) => ({
        scope,
        keys: [...(prev.scope === scope ? prev.keys : NO_KEYS), newKey],
      }));

      // If no API key is currently set, use the new one
      if (!apiKeyRef.current) {
        setApiKey(newKey.key);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateApiKey = async (
    id: string,
    updates: { name?: string; permissions?: string[]; isActive?: boolean; dataScope?: ApiKeyDataScope | null }
  ) => {
    const scope = scopeRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const updated = await apiService.updateApiKey(id, updates);
      if (!scope || scopeRef.current !== scope) return;
      setKeysState((prev) =>
        prev.scope === scope
          ? { scope, keys: prev.keys.map((key) => (key.id === id ? { ...key, ...updated } : key)) }
          : prev
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update API key');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteApiKey = async (id: string) => {
    const scope = scopeRef.current;
    try {
      setIsLoading(true);
      setError(null);
      await apiService.deleteApiKey(id);
      if (!scope || scopeRef.current !== scope) return;
      setKeysState((prev) =>
        prev.scope === scope ? { scope, keys: prev.keys.filter((key) => key.id !== id) } : prev
      );

      // If the deleted key was the current one, clear it
      const deletedKey = apiKeys.find((key) => key.id === id);
      if (deletedKey && apiKeyRef.current === deletedKey.key) {
        setApiKey(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete API key');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value: ApiKeyContextType = {
    apiKey,
    setApiKey,
    apiKeys,
    isLoading,
    error,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    refreshApiKeys,
  };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};
